import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import {
  Button,
  Card,
  HStack,
  Loader,
  Typography,
  VStack,
} from '@vivaahealth/design-system';

import * as api from '../../../api';
import * as logger from '../../../logger';
import * as rootReducer from '../../../reducers/root';

import { verifyRequest } from '../../../utils';

type DispatchProps = {
  setOnCaptchaSuccess: (onCaptchaSuccess: (() => void) | null) => void;
};

interface Props extends DispatchProps {
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  setFormField: (formFieldId: string, value: string) => void;
  navigateToNextPage: () => Promise<void>;
}

const PatientCareProvidersPickerPage: React.FC<Props> = ({
  field,
  setFormField,
  navigateToNextPage,
  setOnCaptchaSuccess,
}) => {
  const [providers, setProviders] =
    useState<api.PrevisitPatientCareTeam.PatientPCP[]>();
  const [isLoading, setIsLoading] = useState(false);

  const { id, previsitFormSetId } = field;

  const handleNoPcpFound = useCallback(async () => {
    const value = JSON.stringify({
      pcpNotFound: true,
    });
    setFormField(id, value);
    try {
      await api.client.Previsit.updateFormField(previsitFormSetId, id, value);
    } catch (e) {
      logger.error(e, 'Failed to update form field');
    }
    await navigateToNextPage();
  }, [id, previsitFormSetId, navigateToNextPage, setFormField]);

  useEffect(() => {
    const fetchProviders = async () => {
      setIsLoading(true);
      try {
        await verifyRequest(
          () => api.client.PatientCareTeam.getPatientCareTeam(id),
          async (providers: api.PrevisitPatientCareTeam.PatientPCP[]) => {
            if (!providers.length) {
              handleNoPcpFound();
            } else {
              setProviders(providers);
              setIsLoading(false);
            }
          },
          setOnCaptchaSuccess,
        );
      } catch (e) {
        logger.error(e, `PatientCareProvidersPickerPage.fetchProviders`);
        handleNoPcpFound();
      }
    };
    if (!isLoading) {
      fetchProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, setOnCaptchaSuccess, handleNoPcpFound]);

  const handlePcpSelected = async (
    selectedPcp?: api.PrevisitPatientCareTeam.PatientPCP,
  ) => {
    const value = selectedPcp
      ? JSON.stringify({
          selectedPcp,
        })
      : '';
    setFormField(id, value);
    try {
      await api.client.Previsit.updateFormField(previsitFormSetId, id, value);
    } catch (e) {
      logger.error(e, 'Failed to update form field');
    }
    await navigateToNextPage();
  };

  if (isLoading) {
    return <Loader label="Loading providers..." className="m-auto" />;
  }

  return (
    <VStack gap={24}>
      <Typography variant="headline-small" bold>
        {field.label}
      </Typography>
      {!!providers &&
        providers.map((prov) => (
          <Card size="large" key={prov.firstName + prov.lastName}>
            <Card.Body>
              <HStack gap={16}>
                <Typography
                  variant="title-large"
                  bold
                  className="my-auto me-auto"
                >
                  {prov.firstName + ' ' + prov.lastName}
                </Typography>
                <Button
                  variant="primary"
                  onClick={() => handlePcpSelected(prov)}
                >
                  Choose provider
                </Button>
              </HStack>
            </Card.Body>
          </Card>
        ))}
      <Button variant="outlined" fullWidth onClick={() => handlePcpSelected()}>
        Choose a different provider
      </Button>
    </VStack>
  );
};

function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setOnCaptchaSuccess: (onCaptchaSuccess: (() => void) | null) =>
      dispatch(rootReducer.setOnCaptchaSuccess(onCaptchaSuccess)),
  };
}

export default connect(
  null,
  mapDispatchToProps,
)(PatientCareProvidersPickerPage);
